import React, { useState } from 'react';
import { PhysicalButton } from './PhysicalButton';
import { GoalDetailModal } from './GoalDetailModal';
import { RemoveGoalModal } from './RemoveGoalModal';

/**
 * ScoreEditModal Component
 *
 * Modal for manually adjusting team scores via real events.
 * + opens GoalDetailModal to create a goal event with player/minute/type.
 * - opens RemoveGoalModal to delete an existing goal event.
 * Triggered by clicking on scoreboard in team headers.
 */
export const ScoreEditModal = ({
  isOpen,
  onClose,
  homeTeamName,
  awayTeamName,
  currentHomeScore,
  currentAwayScore,
  onUpdateScore,
  // New props for event-based score management
  events,
  createEvent,
  deleteEvent,
  eventTypes,
  clock,
  homeLineup,
  awayLineup,
  matchId,
  match,
}) => {
  const [goalDetailModal, setGoalDetailModal] = useState({ isOpen: false, side: null });
  const [removeGoalModal, setRemoveGoalModal] = useState({ isOpen: false, side: null });

  // Check if we have the new event-based props
  const hasEventSupport = !!(createEvent && deleteEvent && eventTypes);

  if (!isOpen) return null;

  // Get team IDs - prefer match object, fallback to lineup
  const homeTeamId = match?.home_team_id || match?.home_team?.id || homeLineup?.[0]?.team_id;
  const awayTeamId = match?.away_team_id || match?.away_team?.id || awayLineup?.[0]?.team_id;

  // Filter goals for a specific team from events
  const getTeamGoals = (side) => {
    if (!events) return [];
    const targetTeamId = side === 'home' ? homeTeamId : awayTeamId;
    console.log(`[ScoreEdit] getTeamGoals(${side}) - targetTeamId:`, targetTeamId, 'events count:', events.length);
    const goals = events.filter((e) => {
      const isGoalType = ['goal', 'penalty_goal', 'own_goal'].includes(e.event_type);
      if (!isGoalType) return false;
      const eventTeamId = e.team_id || e.team?.id;
      console.log(`[ScoreEdit] Event ${e.id}: type=${e.event_type}, eventTeamId=${eventTeamId}, targetTeamId=${targetTeamId}, match=${String(eventTeamId) === String(targetTeamId)}`);
      return eventTeamId && String(eventTeamId) === String(targetTeamId);
    });
    console.log(`[ScoreEdit] Found ${goals.length} goals for ${side}`);
    return goals;
  };

  const handleAddGoal = (side) => {
    if (hasEventSupport) {
      setGoalDetailModal({ isOpen: true, side });
    } else {
      // Fallback to simple increment
      if (side === 'home') {
        onUpdateScore(currentHomeScore + 1, currentAwayScore);
      } else {
        onUpdateScore(currentHomeScore, currentAwayScore + 1);
      }
    }
  };

  const handleRemoveGoal = (side) => {
    if (hasEventSupport) {
      const goals = getTeamGoals(side);
      if (goals.length > 0) {
        // Events available - show picker to remove a specific goal
        setRemoveGoalModal({ isOpen: true, side });
      } else {
        // No goal events tracked (e.g. page opened after goals were scored)
        // Fall back to numeric decrement
        const currentScore = side === 'home' ? currentHomeScore : currentAwayScore;
        if (currentScore > 0) {
          if (side === 'home') {
            onUpdateScore(Math.max(0, currentHomeScore - 1), currentAwayScore);
          } else {
            onUpdateScore(currentHomeScore, Math.max(0, currentAwayScore - 1));
          }
        }
      }
    } else {
      // No event support at all - simple decrement
      if (side === 'home') {
        onUpdateScore(Math.max(0, currentHomeScore - 1), currentAwayScore);
      } else {
        onUpdateScore(currentHomeScore, Math.max(0, currentAwayScore - 1));
      }
    }
  };

  const handleGoalConfirm = async (goalData) => {
    try {
      const eventTypeObj = eventTypes.find(
        (et) => et.name?.toLowerCase() === goalData.eventTypeName
      );
      if (!eventTypeObj) {
        console.error('Event type not found:', goalData.eventTypeName);
        return;
      }

      const side = goalDetailModal.side;
      const teamId = side === 'home' ? homeTeamId : awayTeamId;

      await createEvent({
        matchId,
        eventTypeId: goalData.eventTypeId || eventTypeObj.id,
        playerId: goalData.playerId,
        teamId,
        minute: goalData.minute,
        second: 0,
        half: goalData.half,
      });
    } catch (error) {
      console.error('Error creating goal event:', error);
    }
  };

  const handleGoalRemove = async (eventId) => {
    try {
      await deleteEvent(eventId);
    } catch (error) {
      console.error('Error deleting goal event:', error);
    }
  };

  const currentSidePlayers = goalDetailModal.side === 'home' ? homeLineup : awayLineup;
  const currentSideTeamName = goalDetailModal.side === 'home' ? homeTeamName : awayTeamName;
  const removeSideGoals = removeGoalModal.side ? getTeamGoals(removeGoalModal.side) : [];
  const removeSideTeamName = removeGoalModal.side === 'home' ? homeTeamName : awayTeamName;

  return (
    <>
      <div
        className="fixed inset-0 bg-black/80 flex items-center justify-center z-50"
        onClick={onClose}
      >
        <div
          className="bg-gradient-to-br from-gray-800 via-gray-900 to-gray-800 rounded-lg shadow-2xl border border-amber-600/30 max-w-lg w-full mx-4"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="p-6 border-b border-gray-700 bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 rounded-t-lg">
            <h3 className="text-2xl font-bold text-amber-400 tracking-wide text-center">
              EDIT SCORE
            </h3>
            {hasEventSupport && (
              <p className="text-xs text-gray-500 text-center mt-1">
                Creates/removes goal events
              </p>
            )}
          </div>

          {/* Body */}
          <div className="p-8 space-y-8">
            {/* Home Team Score */}
            <div className="space-y-3">
              <div className="text-sm font-semibold text-blue-400 uppercase tracking-widest text-center">
                {homeTeamName}
              </div>
              <div className="flex items-center justify-center gap-4">
                <PhysicalButton
                  color="red"
                  size="large"
                  onClick={() => handleRemoveGoal('home')}
                  disabled={hasEventSupport && getTeamGoals('home').length === 0 && currentHomeScore === 0}
                >
                  −
                </PhysicalButton>
                <div className="relative bg-black rounded-lg p-4 shadow-[inset_0_2px_8px_rgba(0,0,0,0.8)] border border-gray-800 min-w-[120px]">
                  <div
                    className="seven-segment text-6xl font-bold text-gray-800 tabular-nums leading-none text-center"
                    aria-hidden="true"
                  >
                    88
                  </div>
                  <div className="absolute inset-0 p-4 seven-segment text-6xl font-bold text-blue-400 shadow-[0_0_20px_rgba(96,165,250,0.6)] tabular-nums leading-none text-center">
                    {String(currentHomeScore).padStart(2, '0')}
                  </div>
                </div>
                <PhysicalButton
                  color="green"
                  size="large"
                  onClick={() => handleAddGoal('home')}
                >
                  +
                </PhysicalButton>
              </div>
            </div>

            {/* VS Divider */}
            <div className="text-center text-3xl font-bold text-gray-600">VS</div>

            {/* Away Team Score */}
            <div className="space-y-3">
              <div className="text-sm font-semibold text-red-400 uppercase tracking-widest text-center">
                {awayTeamName}
              </div>
              <div className="flex items-center justify-center gap-4">
                <PhysicalButton
                  color="red"
                  size="large"
                  onClick={() => handleRemoveGoal('away')}
                  disabled={hasEventSupport && getTeamGoals('away').length === 0 && currentAwayScore === 0}
                >
                  −
                </PhysicalButton>
                <div className="relative bg-black rounded-lg p-4 shadow-[inset_0_2px_8px_rgba(0,0,0,0.8)] border border-gray-800 min-w-[120px]">
                  <div
                    className="seven-segment text-6xl font-bold text-gray-800 tabular-nums leading-none text-center"
                    aria-hidden="true"
                  >
                    88
                  </div>
                  <div className="absolute inset-0 p-4 seven-segment text-6xl font-bold text-red-400 shadow-[0_0_20px_rgba(248,113,113,0.6)] tabular-nums leading-none text-center">
                    {String(currentAwayScore).padStart(2, '0')}
                  </div>
                </div>
                <PhysicalButton
                  color="green"
                  size="large"
                  onClick={() => handleAddGoal('away')}
                >
                  +
                </PhysicalButton>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="p-4 border-t border-gray-700 flex justify-end bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 rounded-b-lg">
            <button
              onClick={onClose}
              className="px-6 py-2 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600 font-semibold transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>

      {/* Sub-modals */}
      {hasEventSupport && (
        <>
          <GoalDetailModal
            isOpen={goalDetailModal.isOpen}
            onClose={() => setGoalDetailModal({ isOpen: false, side: null })}
            teamName={currentSideTeamName}
            players={currentSidePlayers}
            clock={clock}
            eventTypes={eventTypes}
            onConfirm={handleGoalConfirm}
          />
          <RemoveGoalModal
            isOpen={removeGoalModal.isOpen}
            onClose={() => setRemoveGoalModal({ isOpen: false, side: null })}
            teamName={removeSideTeamName}
            goals={removeSideGoals}
            onConfirm={handleGoalRemove}
          />
        </>
      )}
    </>
  );
};
